import React, { useRef, useCallback, useEffect, use, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import UserList from './userList.tsx';
import UserProfile from './userProfile';
import MessageBox from './messageBox.tsx';
import './index.css';
import { useRecoilValue, useRecoilCallback, useSetRecoilState, useRecoilState } from 'recoil';
// import { playNotificationSound } from './notificationSound.ts';
import {
  userInformationAtom,
  interveneFilterAtom,
  messageListAtom,
  showMobileViewAtom,
  userDetailAtom,
  userListAtom,
} from '../../Services/_states';
import {
  Box, Grid, useMediaQuery, IconButton,
  Tooltip,
  Popover,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar as MuiAvatar,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useChatSocket } from '../../WebSockets/ChatSocket.ts';
import { chatCountsAtom } from '../../Services/_states/index.js';
import { InterveneActions } from '../../Services/_actions/Intervene/intervene.actions.js';

const InterveneChats = () => {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const ORGANISATION_ID = loggedInUser?.profile?.organisation?.organisation_id ?? '';
  const isMobile = !useMediaQuery('(max-width:1024px)');
  const showMobileView = useRecoilValue(showMobileViewAtom);
  const actions = InterveneActions();
  const navigate = useNavigate();
  const location = useLocation();
  const interveneRef = useRef(null);
  const interveneFilters = useRecoilValue(interveneFilterAtom);
  const socketsRef = useRef(new Map());
  const socketBotNumber = interveneFilters?.botNumber;
  const currentUserDetailsFromAtom = useRecoilValue(userDetailAtom);
  const [usersAnchorEl, setUsersAnchorEl] = useState(null);
  const [currentUsersOnChat, setCurrentUsersOnChat] = useState<string[]>([]);
  const [chatCounts, setChatCounts] = useRecoilState(chatCountsAtom);
  const userList = useRecoilValue(userListAtom);

  const usersPopoverOpen = Boolean(usersAnchorEl);

  // ── Sound refs ───────────────────────────────────────────────────
  // const isInitialLoadRef      = useRef(true);
  // const userListLoadedRef     = useRef(false);
  // const audioUnlockedRef      = useRef(false);
  const interveneFiltersRef   = useRef(interveneFilters);
  // const loadTimerRef          = useRef<ReturnType<typeof setTimeout> | null>(null);
  // const knownPhoneNumbersRef  = useRef<Set<string>>(new Set());

  // Keep filtersRef in sync — avoids stale closure inside recoil callback
  useEffect(() => {
    interveneFiltersRef.current = interveneFilters;
  }, [interveneFilters]);

  // When bot number changes → reset everything
  // Tab switches must NOT reset — agent stays on same bot
  // useEffect(() => {
  //   knownPhoneNumbersRef.current.clear();
  //   isInitialLoadRef.current  = true;
  //   userListLoadedRef.current = false;
  //   if (loadTimerRef.current) {
  //     clearTimeout(loadTimerRef.current);
  //     loadTimerRef.current = null;
  //   }
  // }, [socketBotNumber]);

  // When userList is CLEARED (tab switch) → re-arm the load guard
  // When userList gets POPULATED for first time after clear → start 500ms guard
//   useEffect(() => {
//   return () => {
//     if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
//   };
// }, []);

  // Cleanup timer on unmount
  // useEffect(() => {
  //   return () => {
  //     if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
  //   };
  // }, []);

  // Unlock browser autoplay on first user interaction
  // useEffect(() => {
  //   const unlockAudio = () => {
  //     if (audioUnlockedRef.current) return;
  //     const silent = new Audio();
  //     silent.play().catch(() => {});
  //     audioUnlockedRef.current = true;
  //     window.removeEventListener('click', unlockAudio);
  //     window.removeEventListener('keydown', unlockAudio);
  //   };
  //   window.addEventListener('click', unlockAudio);
  //   window.addEventListener('keydown', unlockAudio);
  //   return () => {
  //     window.removeEventListener('click', unlockAudio);
  //     window.removeEventListener('keydown', unlockAudio);
  //   };
  // }, []);

  // ── WebSocket ────────────────────────────────────────────────────
  const { status, message, sendMessage, disconnect, reconnect } = useChatSocket({
    url: socketBotNumber
      ? `${process.env.REACT_APP_WEBSOCKET_URL}/chat/${socketBotNumber}/`
      : '',
  });

  useEffect(() => {
    if (status === 'Connected' && currentUserDetailsFromAtom?.phone_number && socketBotNumber) {
      sendMessage({
        type:            'active_user',
        phone_number:    currentUserDetailsFromAtom.phone_number,
        bot_number:      socketBotNumber,
        agent_name:      loggedInUser?.username ?? '',
        organisation_id: ORGANISATION_ID,
      });
    }
  }, [status, currentUserDetailsFromAtom?.phone_number, socketBotNumber, loggedInUser, sendMessage]);

  // ── Message handler ──────────────────────────────────────────────
  useEffect(() => {
    if (!message) return;

    if (message?.type === 'chatMessages' && message?.message) {
      const incomingPhone = message.message?.phone_number;

      // Sound fires when:
      // 1. Initial load guard is done (isInitialLoadRef = false)
      // 2. This phone number has never sent a message before (not in knownPhoneNumbersRef)
      // 3. Agent can be on ANY tab — no tab restriction
      // if (
      //   !isInitialLoadRef.current &&
      //   incomingPhone &&
      //   !knownPhoneNumbersRef.current.has(incomingPhone)
      // ) {
      //   // Mark known immediately — prevents duplicate sounds from rapid WS messages
      //   knownPhoneNumbersRef.current.add(incomingPhone);
      //   setTimeout(() => playNotificationSound(), 0);
      // }

      processIncomingMessage(message.message);
      const botNumber = interveneFilters?.botNumber;
      if (botNumber) actions.refreshChatCounts(botNumber);

    } else if (message?.type === 'current_users_on_chat' && message?.users) {
      setCurrentUsersOnChat(message.users || []);

    } else if (message?.type === 'intervene_status' && message?.message) {
      processInterveneStatusUpdate(message?.message);
      const botNumber = interveneFilters?.botNumber;
      if (botNumber) actions.refreshChatCounts(botNumber);
    }
  }, [message]);

  // ── processIncomingMessage ───────────────────────────────────────
  const processIncomingMessage = useRecoilCallback(
    ({ snapshot, set }) =>
      async (incomingMsg) => {
        if (!incomingMsg || !incomingMsg.phone_number || !incomingMsg.msg_id) return;

        const currentUserDetails = await snapshot.getPromise(userDetailAtom);

        // Append to open chat if this user's chat is currently visible
        if (currentUserDetails?.phone_number === incomingMsg.phone_number) {
          const currentMsgList = await snapshot.getPromise(messageListAtom);
          const exists = (currentMsgList?.messages || []).some(
            (m) => m.msg_id === incomingMsg.msg_id
          );
          if (!exists) {
            set(messageListAtom, (prev) => ({
              ...prev,
              messages:    [...(prev?.messages || []), incomingMsg],
              total_count: (prev?.total_count || 0) + 1,
            }));
          }
        }

        // Update user list
        set(userListAtom, (prevUserList) => {
          const currentUsers  = prevUserList?.users || [];
          const existingIndex = currentUsers.findIndex(
            (u) => u.phone_number === incomingMsg.phone_number
          );

          if (existingIndex !== -1) {
            // ── User ALREADY in list — update only ──
            const user         = currentUsers[existingIndex];
            const isActiveChat = currentUserDetails?.phone_number === incomingMsg.phone_number;

            const updatedUser = {
              ...user,
              last_message:     incomingMsg.content     ?? user.last_message,
              msg_id:           incomingMsg.msg_id,
              not_read_count:   isActiveChat
                ? 0
                : user.msg_id !== incomingMsg.msg_id
                  ? (user.not_read_count ?? 0) + 1
                  : user.not_read_count ?? 0,
              last_active_date: incomingMsg.timestamp
                ? new Date(incomingMsg.timestamp * 1000).toISOString()
                : user.last_active_date,
              whatsapp_name:    incomingMsg.whatsapp_name ?? user.whatsapp_name,
              intervene:        incomingMsg.is_intervene  ?? user.intervene,
            };

            return {
              ...prevUserList,
              users: [
                updatedUser,
                ...currentUsers.slice(0, existingIndex),
                ...currentUsers.slice(existingIndex + 1),
              ],
            };

          } else {
            // ── Brand NEW user — add to list ──
            // Sound is handled in the message useEffect above, NOT here
            const newUser = {
              phone_number:     incomingMsg.phone_number,
              whatsapp_name:    incomingMsg.whatsapp_name ?? 'New User',
              last_message:     incomingMsg.content ?? {},
              msg_id:           incomingMsg.msg_id,
              not_read_count:   1,
              last_active_date: incomingMsg.timestamp
                ? new Date(incomingMsg.timestamp * 1000).toISOString()
                : new Date().toISOString(),
              intervene:        incomingMsg.is_intervene ?? false,
              session_start:    false,
              admin_message:    null,
            };

            return {
              ...prevUserList,
              users:       [newUser, ...currentUsers],
              total_count: (prevUserList?.total_count || 0) + 1,
            };
          }
        });
      },
    []
  );

  // ── processInterveneStatusUpdate ─────────────────────────────────
  const processInterveneStatusUpdate = useRecoilCallback(
    ({ snapshot, set }) =>
      async (incomingMsg) => {
        if (!incomingMsg || !incomingMsg.phone_number) return;

      const isIntervene = incomingMsg.intervene ?? false;
      const currentUserDetails = await snapshot.getPromise(userDetailAtom);

        set(userListAtom, (prevUserList) => {
          const currentUsers      = prevUserList?.users || [];
          const existingUserIndex = currentUsers.findIndex(
            (user) => user.phone_number === incomingMsg.phone_number
          );

          if (existingUserIndex !== -1) {
            const userToUpdate = currentUsers[existingUserIndex];
            const updatedUser  = {
              ...userToUpdate,
              intervene:        isIntervene,
              session_start:    incomingMsg.session_start    ?? userToUpdate.session_start,
              intervened_by:    incomingMsg.intervened_by    ?? userToUpdate.intervened_by,
              last_active_date: incomingMsg.last_active_date ?? userToUpdate.last_active_date,
              whatsapp_name:    incomingMsg.whatsapp_name    ?? userToUpdate.whatsapp_name,
            };

            const newUsersArray = [...currentUsers];
newUsersArray[existingUserIndex] = updatedUser;
return { ...prevUserList, users: newUsersArray };

            return { ...prevUserList, users: newUsersArray };

          } else if (isIntervene) {
            const newUser = {
              phone_number:     incomingMsg.phone_number,
              whatsapp_name:    incomingMsg.whatsapp_name    ?? 'New User',
              last_message:     incomingMsg.last_message     ?? {},
              msg_id:           incomingMsg.msg_id           ?? '',
              not_read_count:   incomingMsg.not_read_count   ?? 0,
              last_active_date: incomingMsg.last_active_date ?? new Date().toISOString(),
              intervene:        true,
              session_start:    incomingMsg.session_start    ?? false,
              intervened_by:    incomingMsg.intervened_by    ?? '',
              admin_message:    null,
            };
            return {
              ...prevUserList,
              users:       [newUser, ...(prevUserList?.users || [])],
              total_count: (prevUserList?.total_count || 0) + 1,
            };
          }

        return prevUserList;
      });

      if (currentUserDetails?.phone_number === incomingMsg.phone_number) {
        set(userDetailAtom, {
          ...currentUserDetails,
          intervene: isIntervene,
          session_start: incomingMsg.session_start ?? currentUserDetails.session_start,
          intervened_by: incomingMsg.intervened_by ?? currentUserDetails.intervened_by,
          last_active_date: incomingMsg.last_active_date ?? currentUserDetails.last_active_date,
          whatsapp_name: incomingMsg.whatsapp_name ?? currentUserDetails.whatsapp_name,
        });
      }

      // if (isIntervene) {
      //   set(interveneFilterAtom, (prev) => ({
      //     ...prev,
      //     userType: '',
      //     page: 1,
      //     search: '',
      //     resetUserList: true,
      //   }));

      //   if (!location.pathname.startsWith('/r/intervene-dashboard/intervene')) {
      //     navigate('/r/intervene-dashboard/intervene');
      //   }
      // }
    },
  [navigate, location.pathname]
);

  const handleOpenUsersList = (event: React.MouseEvent) => {
    setUsersAnchorEl(event.currentTarget);
  };

  const handleCloseUsersList = () => {
    setUsersAnchorEl(null);
  };

  return (
    <>
      <Box ref={interveneRef} sx={{ width: '100%' }}>
        {isMobile ? (
          <Grid container>
            <Grid
              item xs={3} sm={3} md={3}
              sx={{ backgroundColor: '#fff', border: '1px solid #CFDDEB', borderRadius: '8px' }}
            >
              <UserList />
            </Grid>

            <Grid item xs={showMobileView?.userProfile ? 6 : 9}>
              <MessageBox currentUsersOnChat={currentUsersOnChat} />
            </Grid>

            {showMobileView?.userProfile && (
              <Grid
                item xs={3}
                sx={{
                  backgroundColor: '#fff',
                  opacity: 1,
                  transition: 'all 1s ease',
                  height: '100%',
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Box sx={{ backgroundColor: '#F5F5F5', borderRadius: '8px', height: '100%' }}>
                  <UserProfile />
                </Box>
              </Grid>
            )}
          </Grid>
        ) : (
          <Grid
            container
            sx={{
              flexWrap:      { xs: 'wrap', sm: 'auto' },
              flexDirection: { xs: 'column', sm: 'auto' },
            }}
          >
            {showMobileView?.userList && (
              <Grid
                item xs={1} sm={3}
                sx={{ flexBasis: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 'auto' } }}
              >
                <UserList />
              </Grid>
            )}

            {(isMobile || showMobileView?.messageBox) && (
              <Grid
                item
                xs={showMobileView?.userList && showMobileView?.userProfile ? 6 : 9}
                sx={{ flexBasis: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: 'auto' } }}
              >
                <MessageBox currentUsersOnChat={currentUsersOnChat} />
              </Grid>
            )}

            {showMobileView?.userProfile && (
              <Grid item xs={1} sm={12} md={12} lg={3} maxWidth={'100%'}>
                <UserProfile />
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default InterveneChats;